import { createCustomBlockProperties } from '../../../utils';
import * as lexical from './lexical';
import { parseLiquidProperties } from './parseLiquidTag';

interface LiquidTag {
    tag: string;
    data: {
        [key: string]: string | number | boolean;
    };
}

/**
 * Returns a new Markdown string with any Liquid tags replaces by
 * custom GitBook elements.
 *
 * So {% hint style="info" %} would become
 * <div data-gb-custom-block data-tag="hint" data-style="info">
 *
 * The approach is to pre-emptively close a tag when we encounter it,
 * and then remove the "implicit" closing tag if we encounter an "explicit"
 * closing tag.
 *
 * Note that whitespace is very important here. We need at least 2 newlines before
 * and after each tag so that the contents is correctly parsed as Markdown.
 */
export function processLiquidTags(markdown: string): string {
    const regex = /{% *(.*?) *(?=[#%}]})%}/g;
    const codeBlockRegex = /(````*|~~~)[\s\S]*?\1/g; // can be ```` if the code block has a nested ``` block inside.
    const listIndentRegex = /^(\s*)([*+-]|\d+\.)\s+/;
    const codeBlockRanges = Array.from(markdown.matchAll(codeBlockRegex)).map(
        (m) => [m.index || 0, (m.index || 0) + m[0].length] as [number, number]
    );

    // Detect indented code blocks
    // In GFM, code blocks can be defined by indentation alone: https://github.github.com/gfm/#indented-code-blocks
    const indentedBlockRanges: [number, number][] = [];
    const listStack: Array<{ indent: number; whitespace: number }> = [];
    const lines = markdown.split('\n');
    let blockStart: number | null = null;
    let blockEnd: number | null = null;
    let charCount = 0;

    const flushBlock = () => {
        if (blockStart !== null && blockEnd !== null) {
            indentedBlockRanges.push([blockStart, blockEnd]);
        }
        blockStart = null;
        blockEnd = null;
    };

    // store blank line characters and add only if the block is still indented.
    // otherwise ignore, so we don't capture new lines at end of block range.
    let blankLineChars = 0;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const previousLine = i !== 0 ? lines[i - 1] : null;
        const whitespaceMatch = line.match(/^\s*/);
        const leadingWhitespace = whitespaceMatch?.[0].length ?? 0;
        const isBlank = /^\s*$/.test(line);
        const listMatch = line.match(listIndentRegex);

        if (!listMatch && !isBlank) {
            while (
                listStack.length > 0 &&
                leadingWhitespace <= listStack[listStack.length - 1].whitespace
            ) {
                listStack.pop();
            }
        }

        if (listMatch) {
            const whitespace = listMatch[1].length;
            while (
                listStack.length > 0 &&
                whitespace <= listStack[listStack.length - 1].whitespace
            ) {
                listStack.pop();
            }
            listStack.push({ whitespace, indent: listMatch[0].length });
        }

        const activeListIndent =
            listMatch?.[0].length ?? listStack[listStack.length - 1]?.indent ?? 0;
        const effectiveIndent = Math.max(leadingWhitespace - activeListIndent, 0);
        const isIndented = effectiveIndent >= 4;

        if (isIndented) {
            // start of the block range
            if (blockStart === null) {
                blockStart = charCount;
            }
            blockEnd = charCount + line.length + blankLineChars;
            blankLineChars = 0;
        } else if (!isBlank) {
            // not indented and not blank = end of the block
            if (previousLine && /^\s*$/.test(previousLine)) {
                // roll back the added blank line if it is at the end of the indented range
                blockEnd = charCount - blankLineChars;
            }
            blankLineChars = 0;
            flushBlock();
        } else if (isBlank && blockStart !== null) {
            // blank line within a code block, keep it going
            blankLineChars += line.length;
        }

        charCount += line.length + 1; // +1 for \n to move to next line
        if (i === lines.length - 1) {
            blankLineChars = 0;
            flushBlock();
        }
    }

    const allCodeBlockRanges = [...codeBlockRanges, ...indentedBlockRanges];

    // Detect inline code spans (e.g. `code` or ``code``)
    const inlineCodeRanges: [number, number][] = [];
    let i = 0;
    while (i < markdown.length) {
        const blockRange = findRange(allCodeBlockRanges, i);
        if (blockRange) {
            i = blockRange[1];
            continue;
        }

        if (isCodeBacktick(markdown, i)) {
            let backticks = 1;
            while (markdown[i + backticks] === '`') {
                backticks++;
            }
            const closing = '`'.repeat(backticks);
            const end = markdown.indexOf(closing, i + backticks);
            if (end !== -1) {
                inlineCodeRanges.push([i, end + backticks]);
                i = end + backticks;
                continue;
            }
        }
        i++;
    }

    const allExcludedRanges = [...allCodeBlockRanges, ...inlineCodeRanges];

    const isInExcludedRange = (pos: number): [number, number] | null => {
        return findRange(allExcludedRanges, pos);
    };

    // The current RegEx match.
    let currentMatch: RegExpExecArray | null = null;

    // The resulting Markdown.
    let result = '';

    // The index in the Markdown string that we have output Markdown up to.
    let haveProcessedUpTo = 0;

    // A stack of open tags. Each entry in the stack is the captured tag and the index of the last character
    // of the tag. The first element in the array is the first open tag.
    const openTagsStack: { tag: string; index: number }[] = [];

    // Iterate over each match (either an opening or closing Liquid tag)
    while ((currentMatch = regex.exec(markdown)) !== null) {
        const index = currentMatch.index;
        const group = currentMatch[1];
        const indexAfter = index + currentMatch[0].length;
        // Skip the match if it is inside a code block or inline code
        const codeRange = isInExcludedRange(index);
        if (codeRange) {
            // we still need to preserve the correct whitespace here
            result += buildResult(markdown.slice(haveProcessedUpTo, codeRange[1]));
            haveProcessedUpTo = codeRange[1];
            regex.lastIndex = codeRange[1];
            continue;
        }

        // If the match is escaped, we ignore it and continue.
        if (markdown[index - 1] === '\\') {
            result += markdown.slice(haveProcessedUpTo, indexAfter);
            haveProcessedUpTo = indexAfter;
            continue;
        }

        // The text between the most recent <gb-custom-block> tag and the current
        // tag.
        const mdDelta = markdown.slice(haveProcessedUpTo, index);

        haveProcessedUpTo = regex.lastIndex;

        // Parse the tag into a <gb-custom-block> html tag
        const parsed = parseLiquidTag(group.trim());

        if (!parsed) {
            // if we couldn't parse the tag, we ignore it and return the original markdown
            result += mdDelta + currentMatch[0];
            continue;
        }

        const { html, closing } = toHtml(parsed);

        // If we've encountered an explicit closing tag, pop the stack and delete the "implicit"
        // closing tag that was automatically added.
        if (closing) {
            // Drop all opened tags captured before this new end tag.
            // It means that they are self-closing tags.
            let lastAddedMatchingTag = openTagsStack.pop();
            while (
                typeof lastAddedMatchingTag === 'object' &&
                `end${lastAddedMatchingTag.tag}` !== parsed.tag
            ) {
                lastAddedMatchingTag = openTagsStack.pop();
            }

            const lastAddedIndex = lastAddedMatchingTag?.index || 0;
            result =
                result.substring(0, lastAddedIndex - '</div>'.length) +
                result.substring(lastAddedIndex);
        }

        // Update the result and the references.
        // Create a new line if there's Markdown content between the tag and the Markdown above.
        result += buildResult(mdDelta, html);

        if (!closing) {
            openTagsStack.push({ tag: parsed.tag, index: result.length });
        }
    }

    // Make sure the remainder of the text is added to the result - this also
    // handles the case where there are no matches.
    result = removeTrailingAndLeadingLineBreaks(result) + markdown.slice(haveProcessedUpTo);
    return result;
}

/**
 * Result constructed from trimmed markdown and normalized line breaks and indentation
 */
function buildResult(md, append = '') {
    const { leading, trailing, content } = getWhitespace(md);
    return `${leading?.break ?? ''}${leading?.indent ?? ''}${content}${trailing?.break ?? ''}${trailing?.indent ?? ''}${append}`;
}

function getWhitespace(md: string) {
    // Normalize CRLF to LF so blank-line and break detection below behaves the
    // same for Windows-style line endings (otherwise `\r\n`-only whitespace
    // never matches the early return or the `^(\n+)` fallback).
    const normalized = md.replace(/\r\n/g, '\n');

    // handle multiple blank new lines with no other content
    if (/^\n{2,}$/.test(normalized)) {
        return { content: normalized };
    }

    const trailing = normalized.match(/(\s+)$/);
    const leading = normalized.match(/^(\s+)/);
    const content = normalized.trim();
    const hasTrimmedContent = content.length > 0 && content.length !== normalized.length;
    return {
        content,
        leading: leading?.[0]
            ? {
                  // A markdown trailing-space line break like `text.  \n` makes the
                  // whitespace start with a space rather than `\n`, so check for any
                  // line break in the captured whitespace, not just the first char.
                  break:
                      hasTrimmedContent && leading[0].includes('\n')
                          ? '\n\n'
                          : leading[0].match(/^(\n+)/)?.[0], // handles blank lines with or without line breaks
                  indent: leading[0].match(/( +)$/)?.[0], // spaces the content is indented
              }
            : null,
        trailing: trailing?.[0]
            ? {
                  break:
                      hasTrimmedContent && trailing[0].includes('\n')
                          ? '\n\n'
                          : trailing[0].match(/^(\n+)/)?.[0], // handles blank lines with or without line breaks
                  indent: trailing[0].match(/( +)$/)?.[0], // spaces after the content is indented
              }
            : null,
    };
}

/**
 * Check whether a character is the edge of a code fence.
 * If it is a backtick and it is not escaped we can consider it the edge of a code fence.
 */
function isCodeBacktick(test: string, index: number) {
    if (index === 0 && test[index] === '`') {
        return true;
    }
    return test[index] === '`' && test[index - 1] !== '\\'; // check it is not an escaped backtick
}

/**
 * Converts a parsed Liquid tag into an HTML string.
 *
 * Make sure that there's a new line before and after each tag so that any
 * content within the HTML element is parsed correctly as Markdown.
 *
 * Opening tags will contain an implict closing tag, so:
 * <div data-gb-custom-block data-tag="hint" data-style="info"></div>
 *
 * See: https://github.com/rehypejs/rehype-raw#rehypeuseraw
 */
function toHtml(parsed: LiquidTag): { html: string; closing: boolean } {
    const { tag, data } = parsed;

    const isEnd = tag.startsWith('end');

    if (isEnd) {
        return { html: '</div>', closing: true };
    }

    let result = `<div data-gb-custom-block data-tag="${tag}"`;

    result = Object.keys(data).reduce((result, key) => {
        return `${result} data-${key}='${escapeHtmlAttribute(String(data[key]))}'`;
    }, result);

    return { html: `${result}></div>`, closing: false };
}

/**
 * Escape a value so it can be safely embedded inside a single-quoted HTML
 * attribute. Without this, an attribute value containing the delimiter (e.g. an
 * `{% if %}` expression with a string literal like `=== 'enterprise'`) would be
 * truncated at the first inner quote when the generated HTML is re-parsed. The
 * entities are decoded back to their original characters by the HTML parser.
 */
function escapeHtmlAttribute(value: string): string {
    return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

/**
 * Does what it says on the tin - removed any trailing/leading line breaks.
 */
function removeTrailingAndLeadingLineBreaks(str: string): string {
    return str.replace(/^\n*|\n*$/g, '');
}

/**
 * Parse the inner text of a tag.
 */
function parseLiquidTag(text: string): LiquidTag | null {
    const match = text.match(lexical.tagLine);

    if (!match) {
        return null;
    }

    const tag = match[1];
    const rawData =
        tag === 'if'
            ? { expression: match[2].trim() }
            : createCustomBlockProperties(parseLiquidProperties(match[2]));

    return {
        tag,

        // Normalize the data keys to remove any data- prefix (including camel case).
        data: Object.entries(rawData).reduce((result, [key, value]) => {
            if (key.startsWith('data-')) {
                // Remove the 'data-' prefix from the key.
                const newKey = key.slice(5);
                result[newKey] = value;
            } else if (key.startsWith('data')) {
                // If createCustomBlockProperties gave us camel case, convert it to hyphenated.
                const keyWithoutData = key.slice(4);
                result[camelToHyphen(keyWithoutData)] = value;
            } else {
                result[key] = value;
            }

            return result;
        }, {}),
    };
}

function camelToHyphen(str: string): string {
    return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function findRange(ranges: [number, number][], pos: number): [number, number] | null {
    for (const range of ranges) {
        if (pos >= range[0] && pos < range[1]) {
            return range;
        }
    }
    return null;
}
